<template>
  <div class="bonus-history-container">
    <div class="page-header">
      <h2>{{ $t('profile.bonushistory') }}</h2>
      <div class="header-actions">
        
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>{{ $t('profile.loadingBonuses') }}</p>
    </div>

    <!-- Empty -->
    <div v-else-if="filteredBonuses.length === 0" class="empty-state">
      <div class="empty-icon">🎁</div>
      <h3>{{ $t('profile.noBonuses') }}</h3>
    </div>

    <!-- Desktop Table -->
    <div v-if="!loading && filteredBonuses.length > 0" class="table-container desktop-table">
      <table class="bonus-table">
        <thead>
          <tr>
            <th>{{ $t('profile.bonusType') }}</th>
            <th>{{ $t('profile.bonusAmount') }}</th>
            
            <th>{{ $t('profile.Date') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(bonus, index) in paginatedBonuses" :key="index">
            <td>{{ $t(`profile.bonusTypes.${bonus.bonusType}`) }}</td>

            <td class="amount">
  <span class="bonus-box">+{{ formatAmount(bonus.bonusAmount) }}</span>
</td>

            
            <td class="date">{{ formatDate(bonus.createdAt) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mobile Cards -->
    <div v-if="!loading && filteredBonuses.length > 0" class="mobile-cards">
      <div v-for="(bonus, index) in paginatedBonuses" :key="index" class="card">
        <div class="card-header">
          <span class="bonus-type">{{ $t(`profile.bonusTypes.${bonus.bonusType}`) }}</span>

          <span class="value amount">
  <span class="bonus-box">+{{ formatAmount(bonus.bonusAmount) }}</span>
</span>

        </div>
        <div class="card-content">
         
          <div class="card-row">
            <span class="label">{{ $t('profile.Date') }}:</span>
            <span class="value date">{{ formatDate(bonus.createdAt) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="!loading && filteredBonuses.length > 0" class="pagination-container">
      <div class="pagination-info">
        {{ $t('profile.shown') }} {{ (currentPage - 1) * itemsPerPage + 1 }}-{{ Math.min(currentPage * itemsPerPage, filteredBonuses.length) }}
        {{ $t('profile.total') }} {{ filteredBonuses.length }} {{ $t('profile.from the record') }}
      </div>
      <div class="pagination">
        <button @click="prevPage" :disabled="currentPage === 1" class="pagination-btn">&lt;</button>
        <button v-for="page in visiblePages" :key="page" @click="goToPage(page)" :class="{ active: currentPage === page }" class="page-btn">
          {{ page }}
        </button>
        <button @click="nextPage" :disabled="currentPage === totalPages" class="pagination-btn">&gt;</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import axios from 'axios';

export default {
  data() {
    return {
      bonuses: [],
      currentPage: 1,
      itemsPerPage: 15,
      loading: true,
      maxVisiblePages: 5
    };
  },
  computed: {
    ...mapGetters(['authUser']),

    filteredBonuses() {
      return this.bonuses || [];
    },

    paginatedBonuses() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredBonuses.slice(start, start + this.itemsPerPage);
    },

    totalPages() {
      return Math.ceil(this.filteredBonuses.length / this.itemsPerPage);
    },

    visiblePages() {
      const half = Math.floor(this.maxVisiblePages / 2);
      let start = Math.max(1, this.currentPage - half);
      let end = Math.min(this.totalPages, start + this.maxVisiblePages - 1);
      if (end - start < this.maxVisiblePages - 1) {
        start = Math.max(1, end - this.maxVisiblePages + 1);
      }
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }
  },
  mounted() {
    this.fetchBonusHistory();
  },
  methods: {
    async fetchBonusHistory() {
      try {
        this.loading = true;
        const userId = this.authUser.user._id;
        const response = await axios.get(`/bonus-history/${userId}`);
        this.bonuses = response.data.bonuses || [];
      } catch (err) {
        console.error('Bonus history error:', err);
      } finally {
        this.loading = false;
      }
    },
    formatAmount(amount) {
      if (amount === null || amount === undefined) return '-';
      return Number(amount).toFixed(2);
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    prevPage() { if (this.currentPage > 1) this.currentPage--; },
    nextPage() { if (this.currentPage < this.totalPages) this.currentPage++; },
    goToPage(page) { this.currentPage = page; }
  }
};
</script>

<style scoped>

.bonus-history-container {
 
  color: #fff;
  
}

.page-header {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.page-header h2 {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: #fff;
}

.header-actions {
  display: flex;
  align-items: center;
}

.items-per-page-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--background2);
  padding: 8px 12px;
  border-radius: 8px;
}

.items-per-page-selector select {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: white;
  padding: 4px 8px;
  cursor: pointer;
}

/* Loading */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-left: 4px solid #01e0a3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #888;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 16px;
}

.empty-state h3 {
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  color: #ddd;
}

.empty-state p {
  margin: 0;
  font-size: 0.95rem;
}

/* Table */
.table-container {
  overflow-x: auto;
  border-radius: 8px;
  margin: 20px 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.bonus-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--background2);
  border-radius: 8px;
  overflow: hidden;
}

.bonus-table th {
  background-color: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-weight: 600;
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.bonus-table td {
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.bonus-table tbody tr:last-child td {
  border-bottom: none;
}

.bonus-table tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.amount {
  font-weight: 600;
  color: var(--active-button);
}

.date {
  color: #aaa;
}

/* Mobile cards */
.mobile-cards {
  display: none;
  flex-direction: column;
  gap: 12px;
  margin: 20px 0;
}

.card {
  background: var(--background2);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.card-header .bonus-type {
  font-weight: 700;
  color: #fff;
}

.card-header .amount {
  font-weight: 700;
  color: #01e0a3;
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.card-row {
  display: flex;
  justify-content: space-between;
}

.card-row .label {
  font-weight: 600;
  color: #fff;
}

.card-row .value {
  font-weight: 500;
  color: #fff;
}

/* Pagination */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.pagination-info {
  font-size: 0.9rem;
  color: #aaa;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn, .page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 8px;
  border: none;
  border-radius: 8px;
  background: var(--background2);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled), .page-btn:hover:not(.active) {
  background: rgba(255, 255, 255, 0.1);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-btn.active {
  background: var(--active-button);
  color: #000;
}

.page-numbers {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ellipsis {
  padding: 0 8px;
  color: #888;
}


.bonus-box {
  display: inline-block;
  background: rgba(0, 255, 194, 0.15);
  color: #01e0a3;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 8px;
}

/* Responsive */
@media (max-width: 768px) {
  .bonus-history-container {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: center;
  }

  .desktop-table {
    display: none;
  }

  .mobile-cards {
    display: flex;
  }

  .pagination-container {
    flex-direction: column;
    align-items: center;
  }

  .pagination-info {
    order: 2;
  }

  .pagination {
    order: 1;
  }
}

@media (max-width: 480px) {
  .page-numbers {
    display: none;
  }

  .pagination-info {
    text-align: center;
  }
}


</style>
